import { Record } from 'immutable';

import Hydrator from 'utils/Hydrator';

import CompanyInfo from 'models/CompanyInfoModel';
import BalanceSheet from 'models/BalanceSheetModel';
import CreditLine from 'models/CreditLineModel';
import CreditReport from 'models/CreditReportModel';
import Facility from 'models/FacilityModel';
import Debtor from 'models/DebtorModel';
import SnapshotIntegrations from 'models/SnapshotIntegrationsModel';
import SnapshotPreview from 'models/SnapshotPreviewModel';
import Insights from 'models/InsightsModel';
import Delta from 'models/DeltaModel';

const SnapshotRecord = Record({
  id: 0,
  deletedAt: null,
  deletedBy: null,
  deltas: [],
  entityId: 0,
  generatedAt: new Date(),
  generatedBy: null,
  creditReports: [],
  debtors: [],
  debtorsTotalRevenue: 1,
  integrations: null,
  providerCreditLines: [],
  facilities: [],
  balanceSheet: null,
  companyInfo: null,
  preview: null,
  insights: null,
});

export default class Snapshot extends SnapshotRecord {
  static deserialize(jsonObject) {
    if (typeof jsonObject === 'undefined' || jsonObject === null) {
      return undefined;
    }

    const mapProperties = {
      balanceSheet: BalanceSheet.deserialize,
      facilities: Hydrator.fromArray(Facility.deserialize),
      creditReports: Hydrator.fromArray(CreditReport.deserialize),
      debtors: Hydrator.fromArray(Debtor.deserialize),
      deltas: Hydrator.fromArray(Delta.deserialize),
      deletedAt: Hydrator.timestamp2Date,
      generatedAt: Hydrator.timestamp2Date,
      integrations: SnapshotIntegrations.deserialize,
      preview: SnapshotPreview.deserialize,
      companyInfo: CompanyInfo.deserialize,
      providerCreditLines: Hydrator.fromArray(CreditLine.deserialize),
      insights: Insights.deserialize,
    };

    const additionalProperties = {
      debtorsTotalRevenue:
        (jsonObject.debtors || []).reduce((acc, elem) => +acc + +elem.revenue || 0, 0) || 1,
    };

    const hydrated = Object.keys(jsonObject).reduce(
      Hydrator.assign(jsonObject, mapProperties),
      additionalProperties,
    );

    return new Snapshot(hydrated);
  }

  get roundedPreviewFacility() {
    const cashPositionChange = this.preview?.financialInsight?.cashPositionChange;

    return cashPositionChange && cashPositionChange > 0 ? cashPositionChange : 0;
  }

  get companyName() {
    return this.companyInfo?.name || '';
  }

  get insightType() {
    return this.preview?.financialInsight?.type || '';
  }

  get creditScoreGrade() {
    return this.preview?.creditScoreGrade || '';
  }

  get lastFiledAccountsDate() {
    return this.preview?.lastFiledAccountsDate || new Date(0);
  }

  get quickRatio() {
    return +(this.preview?.ratios?.quick || 0);
  }
}
